import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import { default as MUITextField } from '@mui/material/TextField'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { FormikValues } from 'formik'
import { SyntheticEvent } from 'react'

import Typography from '@mui/material/Typography'
import { i18WithParams as t } from '@shared/locale'
import { TextField, TextInputNumber, TextInputPhone } from 'components-ui'
import { Types } from 'service-api'
import { alteredOptions, petSexOptions } from './schema'

interface TPetProfileProps {
  breeds: Types.BreedClientDto[]
  formik: FormikValues
  lpp?: Types.LocationPetProfileClientDto
}

export const PetProfileForm = ({ formik, lpp, breeds }: TPetProfileProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChangeSelect = (_ev: SyntheticEvent, field: string, value: any) => {
    if (!value) {
      return
    }
    formik.setFieldValue(field, value)
  }
  const onDOBHandler = (value: Date | null) => formik.setFieldValue('dob', value)

  return (
    <>
      <TextField
        id="displayName"
        label={t('profile.pet.form.displayName')}
        variant="outlined"
        sx={{ flex: 1 }}
        value={formik.values.displayName}
        onChange={formik.handleChange}
        error={formik.touched.displayName && Boolean(formik.errors.displayName)}
        helperText={formik.touched.displayName && formik.errors.displayName}
        fullWidth
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} useFlexGap>
        {lpp && lpp.breedReadOnly ? (
          <TextField
            id="breed"
            label={t('profile.pet.form.breed')}
            variant="outlined"
            sx={{ flex: 1, mb: 2 }}
            value={formik.values.breed?.displayName}
            fullWidth
            readOnly
          />
        ) : (
          <Autocomplete<Types.LocationSpeciesClientDto>
            id="breed"
            sx={{ flex: 1 }}
            includeInputInList
            clearIcon={null}
            options={breeds}
            value={formik.values.breed}
            onChange={(evt, value) => onChangeSelect(evt, 'breed', value)}
            onBlur={formik.handleBlur}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id
            }}
            getOptionKey={(option) => option.id || ''}
            getOptionLabel={(option) => option.displayName || ''}
            renderInput={(params) => (
              <TextField
                {...params}
                id="breed"
                name="breed"
                label={t('profile.pet.form.breed')}
                variant="outlined"
                fullWidth
                error={formik.touched.breed && Boolean(formik.errors.breed)}
                helperText={formik.touched.breed && formik.errors.breed ? String(formik.errors.breed) : null}
                inputProps={{
                  ...params.inputProps,
                }}
              />
            )}
          />
        )}
        {lpp && lpp.weightReadOnly ? (
          <TextField
            id="weight"
            label={t('profile.pet.form.weight')}
            variant="outlined"
            sx={{ flex: 1 }}
            value={formik.values.weight}
            fullWidth
            readOnly
          />
        ) : (
          <TextInputNumber
            id="weight"
            label={t('profile.pet.form.weight')}
            variant="outlined"
            sx={{ flex: 1 }}
            value={formik.values.weight}
            onChange={formik.handleChange}
            error={formik.touched.weight && Boolean(formik.errors.weight)}
            helperText={formik.touched.weight && formik.errors.weight}
            fullWidth
          />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} useFlexGap>
        <TextField
          id="color"
          label={t('profile.pet.form.color')}
          variant="outlined"
          sx={{ flex: 1 }}
          value={formik.values.color}
          onChange={formik.handleChange}
          error={formik.touched.color && Boolean(formik.errors.color)}
          helperText={formik.touched.color && formik.errors.color}
          fullWidth
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={formik.values.dob}
            onChange={onDOBHandler}
            renderInput={(params) => {
              return (
                <MUITextField
                  {...params}
                  id="dob"
                  name="dob"
                  label={t('profile.pet.form.dob')}
                  variant="outlined"
                  sx={{ flex: 1 }}
                  error={formik.touched.dob && Boolean(formik.errors.dob)}
                  helperText={formik.touched.dob && formik.errors.dob ? String(formik.errors.dob) : null}
                  fullWidth
                />
              )
            }}
          />
        </LocalizationProvider>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={2} useFlexGap>
        <Autocomplete
          id="sex"
          sx={{ flex: 1 }}
          includeInputInList
          clearIcon={null}
          options={petSexOptions}
          value={formik.values.sex}
          onChange={(evt, value) => onChangeSelect(evt, 'sex', value)}
          onBlur={formik.handleBlur}
          isOptionEqualToValue={(option, value) => {
            return option.id === value.id
          }}
          getOptionKey={(option) => option.id || ''}
          getOptionLabel={(option) => option.label || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              id="sex"
              name="sex"
              label={t('profile.pet.form.sex')}
              variant="outlined"
              error={formik.touched.sex && Boolean(formik.errors.sex)}
              helperText={formik.touched.sex && formik.errors.sex ? String(formik.errors.sex) : null}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
        <Autocomplete
          id="altered"
          sx={{ flex: 1 }}
          includeInputInList
          clearIcon={null}
          options={alteredOptions}
          value={formik.values.altered}
          onChange={(evt, value) => onChangeSelect(evt, 'altered', value)}
          onBlur={formik.handleBlur}
          isOptionEqualToValue={(option, value) => {
            return option.id === value.id
          }}
          getOptionKey={(option) => option.id || ''}
          getOptionLabel={(option) => option.label || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              id="altered"
              name="altered"
              label={t('profile.pet.form.altered')}
              variant="outlined"
              error={formik.touched.altered && Boolean(formik.errors.altered)}
              helperText={formik.touched.altered && formik.errors.altered ? String(formik.errors.altered) : null}
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      </Stack>

      <a id="vet" />
      <Typography variant="h6" sx={{ mt: 1, mb: 3 }}>
        {t('profile.pet.veterinarian')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
        <TextField
          id="vetName"
          label={t('profile.pet.form.vetName')}
          variant="outlined"
          sx={{ flex: 1 }}
          value={formik.values.vetName}
          onChange={formik.handleChange}
          error={formik.touched.vetName && Boolean(formik.errors.vetName)}
          helperText={formik.touched.vetName && formik.errors.vetName}
          fullWidth
        />
        <TextInputPhone
          id="vetPhone"
          name="vetPhone"
          label={t('profile.pet.form.vetPhone')}
          variant="outlined"
          sx={{ flex: 1 }}
          value={formik.values.vetPhone}
          onChange={formik.handleChange}
          error={formik.touched.vetPhone && Boolean(formik.errors.vetPhone)}
          helperText={formik.touched.vetPhone && formik.errors.vetPhone}
          fullWidth
        />
      </Stack>
    </>
  )
}
